import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PageContent,
  CisContainer,
  Spinner,
  Button,
  ButtonIcon,
  Row,
  FormHeader,
  FormActions,
} from 'components';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import { globalPropTypes, checkPermissions } from 'helpers';
import { formatDateRange, formatDateTime } from 'helpers/date';
import { useAccessGuard } from 'hooks';
import { getMaMeetingApplicationsForMeeting } from 'api/services/meeting';
import { describeSubmitterShort } from '../Application/submitter';
import MeetingsTable, { ApplicantCell } from '../Meetings.styled';

/** The list names the month a meeting is held in, not the exact day. */
const MONTH_FORMAT = 'MMMM YYYY';

/**
 * Every application the Member Association of the reader sent for one meeting.
 *
 * Everybody holding a seat applies separately, so this is where colleagues see
 * what the others filled in. Only the author of an application may change it;
 * the rest are read only from here.
 */
const Applications = ({ meetingType, userPermissions }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { meetingId } = useParams();

  const [loading, setLoading] = useState(true);
  const [meeting, setMeeting] = useState(null);
  const [items, setItems] = useState([]);

  const [allowed, renderAccessDeniedModal] = useAccessGuard(
    checkPermissions({ roles: [`ma_meeting_view_${meetingType}`] }, userPermissions || []),
  );

  useEffect(() => {
    if (!allowed) return undefined;

    let current = true;
    setLoading(true);

    getMaMeetingApplicationsForMeeting(meetingId)
      .then((response) => response.json())
      .then(({ results }) => {
        if (!current) return;

        setMeeting((results && results.result && results.result.meeting) || null);
        setItems((results && results.result && results.result.items) || []);
        setLoading(false);
      })
      .catch(() => {
        if (!current) return;

        setItems([]);
        setLoading(false);
      });

    return () => {
      current = false;
    };
  }, [meetingId, allowed]);

  const basePath = `/ma/meetings/${meetingType}`;

  if (!allowed) {
    return <PageContent>{renderAccessDeniedModal()}</PageContent>;
  }

  return (
    <PageContent>
      <CisContainer>
        <FormHeader.Wrapper>
          <FormHeader.Title>
            {meeting ? meeting.name : t('page.ma.meetings.applications.title')}
          </FormHeader.Title>
          <FormHeader.Description>
            {t('page.ma.meetings.applications.description', {
              date: meeting ? formatDateRange(meeting.dateFrom, meeting.dateTo, MONTH_FORMAT) : '',
            })}
          </FormHeader.Description>
        </FormHeader.Wrapper>

        <MeetingsTable tablebreakpoint="lg" actionsWidth="10rem" compactHeader>
          <MeetingsTable.Head>
            <MeetingsTable.Row>
              <MeetingsTable.Heading>
                <TableHeaderCell label={t('page.ipma.meetings.list.applicant')} isAction />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell label={t('page.ma.meetings.list.status')} isAction />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell label={t('page.ipma.meetings.application.submittedAt')} isAction />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell label={t('page.ma.meetings.list.actions')} isAction />
              </MeetingsTable.Heading>
            </MeetingsTable.Row>
          </MeetingsTable.Head>
          <MeetingsTable.Body>
            {loading ? (
              <MeetingsTable.Row>
                <MeetingsTable.Cell colSpan={4}>
                  <Spinner />
                </MeetingsTable.Cell>
              </MeetingsTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <MeetingsTable.Row>
                <MeetingsTable.Cell colSpan={4}>{t('global.noResultsFound')}</MeetingsTable.Cell>
              </MeetingsTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <MeetingsTable.Row key={item.id}>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ipma.meetings.list.applicant')}:
                      </MeetingsTable.Label>
                      <ApplicantCell title={describeSubmitterShort(item.submittedBy) || undefined}>
                        {describeSubmitterShort(item.submittedBy) || '-'}
                        {item.mine ? ` (${t('page.ma.meetings.applications.mine')})` : ''}
                      </ApplicantCell>
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ma.meetings.list.status')}:
                      </MeetingsTable.Label>
                      {item.statusLabel}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ipma.meetings.application.submittedAt')}:
                      </MeetingsTable.Label>
                      {formatDateTime(item.submittedAt) || '-'}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ma.meetings.list.actions')}:
                      </MeetingsTable.Label>
                      <Row style={{ gap: '3px', justifyContent: 'center' }}>
                        <ButtonIcon
                          icon={item.editable ? 'edit' : 'eye'}
                          secondary
                          as={Button}
                          title={t(
                            item.editable
                              ? 'page.ma.meetings.list.edit'
                              : 'page.ma.meetings.list.view',
                          )}
                          onClick={() =>
                            history.push(
                              `${basePath}/application/${item.id}/${
                                item.editable ? 'edit' : 'view'
                              }`,
                            )
                          }
                        />
                      </Row>
                    </MeetingsTable.Cell>
                  </MeetingsTable.Row>
                ))
              : null}
          </MeetingsTable.Body>
        </MeetingsTable>

        <FormActions>
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            onClick={() => history.push(basePath)}
          >
            {t('page.ma.meetings.application.back')}
          </Button>
        </FormActions>
      </CisContainer>
    </PageContent>
  );
};

Applications.propTypes = {
  meetingType: PropTypes.oneOf(['cod', 'csp']).isRequired,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
};

const mapStateToProps = ({ user }) => ({
  userPermissions: user.userPermissions,
});

export default connect(mapStateToProps, null)(Applications);
